/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ComboBox as CarbonComboBox } from 'carbon';
import { propsUtils } from '@ba-ui-toolkit/utils';
import { findDOMNode } from 'react-dom';
import './ComboBox.scss';

const { sanitize, BaPropTypes } = propsUtils;

export default class ComboBox extends Component {
	static __ComboBox__ = true;

	static propTypes = {
		/** Options to display in the dropdown menu */
		startingValues: PropTypes.array,
		/** Determines if the dropdown list is numeric or contains text */
		type: PropTypes.oneOf(['text', 'numeric']).isRequired,
		/** Selected value */
		selectedValue: PropTypes.object,
		/** Combobox ID name */
		id: BaPropTypes.deprecated(' is ignored and not applicable to carbon combobox'),
		/** Optional icon on the left side of the box */
		inputIconLeft: BaPropTypes.deprecated(' is ignored and not applicable to carbon combobox'),
		/** Short hint that describes the expected value of the text field */
		placeholder: PropTypes.string,
		/** Flag to disable or enable combobox */
		disabled: PropTypes.bool,
		/** Flag to group the dropdown list items */
		grouped: BaPropTypes.deprecated(' is ignored and not applicable to carbon combobox'),
		/** Flag to disable or enable combobox filter */
		disableFilter: BaPropTypes.deprecated(' is ignored and not applicable to carbon combobox'),
		/** Flag to validate text */
		hasValidationError: PropTypes.bool,
		/** validation error text */
		validationErrorText: PropTypes.string,
		/** Custom render function for header */
		dropdownHeaderRenderer: BaPropTypes.deprecated(
			' is ignored and not applicable to carbon combobox'
		),
		/** Custom render function for footer */
		dropdownFooterRenderer: BaPropTypes.deprecated(
			' is ignored and not applicable to carbon combobox'
		),
		/** Minimum value for numeric combobox */
		min: PropTypes.number,
		/** Maximum value for numeric combobox */
		max: PropTypes.number,
		/** Callback function when a change occurs in combobox */
		onChange: PropTypes.func,
		/** Custom class names */
		className: PropTypes.string,
		/** Custom css styles */
		style: PropTypes.object,
		/** allow empty / blank input */
		allowEmpty: BaPropTypes.deprecated(' is ignored and not applicable to carbon combobox'),
		/** Callback function when combobox text is changed */
		onTextChange: PropTypes.func,
		/** Custom width of combobox */
		customWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		/** Size of combobox */
		size: PropTypes.oneOf(['small', 'normal']),
		/** Extra options for text input */
		textInputExtraOptions: BaPropTypes.deprecated(
			' is ignored and not applicable to carbon combobox'
		),
		/** Extra options for number input */
		numberInputExtraOptions: BaPropTypes.deprecated(
			' is ignored and not applicable to carbon combobox'
		),
		isAttachedToDomNode: BaPropTypes.deprecated(
			' is ignored and not applicable to carbon combobox'
		),
		ariaLabelOfDropdown: PropTypes.string,
	};

	static defaultProps = {
		size: 'normal',
		type: 'text',
		className: '',
		style: {},
		textInputExtraOptions: {},
		numberInputExtraOptions: {},
	};

	_dropdownOnChange = newValue => {
		const { onChange } = this.props;

		onChange && onChange(newValue.selectedItem || {});
	};

	_onTextChange = value => {
		const { onTextChange, type, min, max } = this.props;
		let newValue = value;

		if (type === 'numeric' && value !== '') {
			if (isNaN(parseFloat(value))) {
				return;
			}

			if (parseFloat(value) != value) {
				return;
			}

			if (min != null && parseFloat(value) < min) {
				newValue = min;
			} else if (max != null && parseFloat(value) > max) {
				newValue = max;
			}
		}

		onTextChange && onTextChange(newValue);
	};

	_dropdownRef = node => {
		this._dropdown = node;
		if (node) {
			findDOMNode(node).classList.add('ba-toolkit-combobox-compat');
			if (this.props.customWidth) {
				node.style.setProperty('width', this.props.customWidth, 'important');
			}
		}
	};

	render() {
		const {
			type,
			grouped,
			size,
			className,
			startingValues,
			dropdownHeaderRenderer,
			dropdownFooterRenderer,
			hasValidationError,
			validationErrorText,
			placeholder,
			style,
			selectedValue,
			ariaLabelOfDropdown,
			onTextChange,
			...others
		} = this.props;

		const dropdownProps = {
			'data-tid': 'ba-common-combobox__list-dropdown',
			...sanitize(others, ComboBox),
			placeholder,
			selectedItem: selectedValue && selectedValue.label ? selectedValue : undefined,
			items: startingValues,
			onChange: this._dropdownOnChange,
			className,
			invalid: hasValidationError,
			invalidText: validationErrorText,
			size: size === 'small' ? 'sm' : 'lg',
			style,
			ref: this._dropdownRef,
			ariaLabel: ariaLabelOfDropdown,
			onInputChange: this._onTextChange,
		};

		return <CarbonComboBox {...dropdownProps} />;
	}
}
